import {graphql, useFragment} from 'react-relay'
import {useIssueState} from '@github-ui/use-issue-state'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {LABELS} from '../../../constants/labels'
import {getIssueTitle} from '../../../utils/helpers'
import type {RelationshipsListAllViewItemFragment$key} from './__generated__/RelationshipsListAllViewItemFragment.graphql'
import type {RelationshipsListAllViewItemFragments$key} from './__generated__/RelationshipsListAllViewItemFragments.graphql'

export const PAGE_SIZE = 25

export const RelationshipsListAllViewItemFragments = graphql`
  fragment RelationshipsListAllViewItemFragments on Issue @relay(plural: true) {
    id
    ...RelationshipsListAllViewItemFragment
  }
`

export const RelationshipsListAllViewItemFragment = graphql`
  fragment RelationshipsListAllViewItemFragment on Issue {
    id
    url
    number
    title
    titleHTML
    state
    stateReason
    repository {
      nameWithOwner
    }
  }
`

type RelationshipsListAllViewProps = {
  accessibleTitle: string
  repositoryNameWithOwner: string
  issueFragments: RelationshipsListAllViewItemFragments$key
}
export function RelationshipsListAllView({
  accessibleTitle,
  repositoryNameWithOwner,
  issueFragments,
}: RelationshipsListAllViewProps) {
  const fragments = useFragment(RelationshipsListAllViewItemFragments, issueFragments)

  return (
    <ListView title={accessibleTitle} variant="compact">
      {fragments.map(fragment => (
        <RelationshipsListItem
          key={fragment.id}
          issueFragment={fragment}
          parentIssueRepositoryNameWithOwner={repositoryNameWithOwner}
        />
      ))}
    </ListView>
  )
}

type RelationshipsListItemProps = {
  issueFragment: RelationshipsListAllViewItemFragment$key
  parentIssueRepositoryNameWithOwner?: string
}
function RelationshipsListItem({issueFragment, parentIssueRepositoryNameWithOwner}: RelationshipsListItemProps) {
  const issue = useFragment(RelationshipsListAllViewItemFragment, issueFragment)

  const {sourceIcon} = useIssueState({
    state: issue.state,
    stateReason: issue.stateReason,
  })
  const IconComponent = sourceIcon('Issue')

  const canonicalIssueReference = getCanonicalIssueReference(
    parentIssueRepositoryNameWithOwner,
    issue.repository.nameWithOwner,
    issue.number,
  )

  return (
    <ListItem title={<RelationshipsListItemTitle issue={issue} />}>
      <ListItemLeadingContent>
        <ListItemLeadingVisual icon={IconComponent} />
      </ListItemLeadingContent>

      <ListItemMainContent>
        <ListItemDescription>{canonicalIssueReference}</ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

type RelationshipsListItemTitleProps = {
  issue: {title: string; titleHTML: string; url: string}
}
function RelationshipsListItemTitle({issue}: RelationshipsListItemTitleProps) {
  const titleValue = getIssueTitle(issue.title, issue.titleHTML)
  return <ListItemTitle value={titleValue} href={issue.url} anchorClassName="markdown-title" />
}

type RelationshipsListViewLoadingSkeletonProps = {
  totalItems: number
}
export function RelationshipsListViewLoadingSkeleton({totalItems}: RelationshipsListViewLoadingSkeletonProps) {
  const rows = []
  const firstPageTotalRows = Math.min(PAGE_SIZE, totalItems)
  for (let i = 0; i < firstPageTotalRows; i += 1) {
    rows.push(<RelationshipListItemSkeleton key={i} />)
  }
  return (
    <ListView title={LABELS.relationLoadingList} variant="compact">
      {rows}
    </ListView>
  )
}

function RelationshipListItemSkeleton() {
  return (
    <ListItem
      title={
        <ListItemTitle value={''}>
          <LoadingSkeleton width="30ch" />
        </ListItemTitle>
      }
      metadata={
        <>
          <ListItemMetadata>
            <LoadingSkeleton variant="rounded" width="8ch" />
          </ListItemMetadata>
          <ListItemMetadata>
            <LoadingSkeleton variant="elliptical" width="20px" height="20px" />
          </ListItemMetadata>
        </>
      }
    />
  )
}

const getCanonicalIssueReference = (
  parentIssueRepositoryNameWithOwner: string = '',
  issueRepositoryNameWithOwner: string,
  issueNumber: number,
) => {
  const isSameRepositoryAsOwnerIssue =
    !parentIssueRepositoryNameWithOwner || parentIssueRepositoryNameWithOwner === issueRepositoryNameWithOwner
  return `${isSameRepositoryAsOwnerIssue ? '' : issueRepositoryNameWithOwner}#${issueNumber}`
}

try{ RelationshipsListAllView.displayName ||= 'RelationshipsListAllView' } catch {}
try{ RelationshipsListItem.displayName ||= 'RelationshipsListItem' } catch {}
try{ IconComponent.displayName ||= 'IconComponent' } catch {}
try{ RelationshipsListItemTitle.displayName ||= 'RelationshipsListItemTitle' } catch {}
try{ RelationshipsListViewLoadingSkeleton.displayName ||= 'RelationshipsListViewLoadingSkeleton' } catch {}
try{ RelationshipListItemSkeleton.displayName ||= 'RelationshipListItemSkeleton' } catch {}